/** Итог партии — победа и поражение.
 *
 * Оба варианта живут в одной шторке поверх доски: игрок должен видеть,
 * что он решил (или где остановился), а не смотреть в пустой экран.
 *
 * Разбор результата показан построчно, включая множители. Без него очки
 * выглядят случайным числом, и игроку нечего оптимизировать: он не узнает,
 * что аккуратность стоит дороже спешки.
 */

import type { GameState, Progress, ScoreBreakdown } from '../api/types'
import { Sheet } from './Sheet'
import { Crystal } from './ui'
import { formatMultiplier, formatNumber, formatTime } from '../game/format'
import { pluralOf, t } from '../i18n'

export interface WinPanelProps {
  game: GameState
  score: ScoreBreakdown
  progress: Progress | null
  previousBest: number | null
  onAgain: () => void
  onExit: () => void
  onShare: () => void
}

export function WinPanel({
  game,
  score,
  progress,
  previousBest,
  onAgain,
  onExit,
  onShare,
}: WinPanelProps) {
  const level = t(`level.${game.difficulty}`)

  return (
    <Sheet variant="result">
      <div className="result__headline">
        <span className="result__kicker">
          {game.mode === 'daily' ? t('result.dailySolved') : t('result.levelSolved', { level })}
        </span>
        <span className="result__title">{t('result.solved')}</span>
        <span className="result__score num">{formatNumber(score.score)}</span>
        <span className="hint-text">{t('result.points')}</span>
      </div>

      <div className="result__rows">
        <Row label={t('result.time')} value={formatTime(game.elapsed_ms)} numeric />
        <Row label={t('result.mistakes')} value={String(score.mistakes)} />
        <Row label={t('result.hints')} value={String(score.hints_used)} />
        <Row label={t('result.speed')} value={formatMultiplier(score.speed_factor)} />
        <Row label={t('result.accuracy')} value={formatMultiplier(score.accuracy_factor)} />
        {score.mode_factor > 1 && (
          <Row label={t('result.mode')} value={formatMultiplier(score.mode_factor)} />
        )}
        {score.no_hint_bonus > 0 && (
          <Row
            label={t('result.noHintBonus')}
            value={`+${score.no_hint_bonus}`}
            accent="crystal"
          />
        )}
        <Row label={t('result.coins')} value={`+${score.coins}`} accent="crystal" />
      </div>

      {progress && (
        <div className="stack">
          {progress.is_new_record && (
            <div className="achievement achievement--done">
              <span className="badge badge--done">★</span>
              <span className="list__grow">
                <span className="achievement__title" style={{ display: 'block' }}>
                  {t('result.record', { level })}
                </span>
                <span className="hint-text">
                  {previousBest
                    ? t('result.recordFrom', {
                        old: formatTime(previousBest),
                        new: formatTime(game.elapsed_ms),
                      })
                    : t('result.recordFirst')}
                </span>
              </span>
            </div>
          )}

          {(progress.streak > 0 || progress.unlocked.length > 0) && (
            <div className="achievement">
              <span className="list__grow">
                <span className="achievement__title" style={{ display: 'block' }}>
                  {t('result.streak', {
                    days: progress.streak,
                    word: pluralOf('plural.days', progress.streak),
                  })}
                </span>
                {progress.unlocked.length > 0 && (
                  <span className="hint-text">
                    {t('result.achievement', { title: progress.unlocked[0].title })}
                  </span>
                )}
              </span>
              {progress.bonus_coins > 0 && (
                <span className="achievement__reward">
                  <Crystal>{`+${progress.bonus_coins}`}</Crystal>
                </span>
              )}
            </div>
          )}
        </div>
      )}

      <div className="stack">
        <button type="button" className="button button--primary" onClick={onAgain}>
          {t('result.again')}
        </button>
        <div className="button-row">
          {/* Похвастаться результатом и позвать друга — одно действие:
              ссылка в карточке открывает получателю ту же задачу. */}
          <button type="button" className="button" onClick={onShare}>
            {t('result.share')}
          </button>
          <button type="button" className="button" onClick={onExit}>
            {t('result.toLevels')}
          </button>
        </div>
      </div>
    </Sheet>
  )
}

export interface LossPanelProps {
  game: GameState
  progressCoins: number
  onRestart: () => void
  onExit: () => void
}

/**
 * Поражение показывается спокойно и без встряски.
 *
 * Игрок и так расстроен; добивать его анимацией и восклицательными знаками
 * значит закрыть приложение за него. Экран объясняет правило, показывает,
 * сколько он успел, и предлагает ровно два выхода.
 */
export function LossPanel({ game, progressCoins, onRestart, onExit }: LossPanelProps) {
  return (
    <Sheet variant="result">
      <div className="stack stack--tight">
        <span className="result__title">{t('loss.title')}</span>
        <p className="hint-text" style={{ margin: 0, fontSize: 'var(--size-body)', lineHeight: 1.45 }}>
          {t('loss.hint')}
        </p>
      </div>

      <div className="result__rows">
        <Row
          label={t('loss.filled')}
          value={t('loss.filledValue', { filled: game.filled_count })}
        />
        <Row label={t('result.time')} value={formatTime(game.elapsed_ms)} numeric />
        {progressCoins > 0 && (
          <Row label={t('loss.progressCoins')} value={`+${progressCoins}`} accent="crystal" />
        )}
      </div>

      <div className="stack">
        <button type="button" className="button button--primary" onClick={onRestart}>
          {t('loss.restart')}
        </button>
        <button type="button" className="button" onClick={onExit}>
          {t('result.toLevels')}
        </button>
      </div>
    </Sheet>
  )
}

function Row({
  label,
  value,
  numeric = false,
  accent,
}: {
  label: string
  value: string
  numeric?: boolean
  accent?: 'crystal'
}) {
  return (
    <div className="result__row">
      <span className="result__row-label">{label}</span>
      <span
        className={`result__row-value ${numeric ? 'num' : ''} ${accent === 'crystal' ? 'crystal' : ''}`.trim()}
      >
        {accent === 'crystal' ? `◆ ${value}` : value}
      </span>
    </div>
  )
}
